import "./Header.scss";
import { WmTabPanel } from "@watermarkinsights/ripple-react";
import FacultySuccess from "@/features/faculty-success/FacultySuccess";
import ContinuousImprovement from "@/features/continuous-improvement/ContinuousImprovement";
import InsightsTabComponent from "../tabComponent/InsightsTabComponent";
import { useIntl } from "react-intl";
import { useReducer, useEffect } from "react";
import { sendRequest } from "@/common/http";
import { getDefaultReportingYear } from "./ReportingYears";
import {
  Institution,
  Product,
  ReportingYear,
  TabOption,
  defaultReportingYear
} from "../interfaces/Interface";
import { useNavigate } from "react-router";
import { useContext } from "react";
import { AppContext, AppContextInterface } from "@/contexts/AppContext";
import { checkIfCesProductIntegrated, checkIfFsProductIntegrated } from "../helper";

interface OrgDetailContentState {
  tabOptions: TabOption[];
  reportingYears: ReportingYear[] | null;
  defaultReportingYear: ReportingYear;
  ryApiError: boolean;
}

interface OrgProfileAction {
  type: string;
  facultyTabOption: TabOption;
  continuousImprovementTabOption: TabOption;
  selectedReportingYearUuid: string | null;
  data: ReportingYear[];
  ryApiError: boolean;
}

const defaultInitialTabOption: TabOption = {
  name: "",
  id: ""
};

const defaultAction: OrgProfileAction = {
  type: "",
  facultyTabOption: defaultInitialTabOption,
  continuousImprovementTabOption: defaultInitialTabOption,
  selectedReportingYearUuid: "",
  data: [],
  ryApiError: false
};

const reducer = (state: OrgDetailContentState, action: OrgProfileAction) => {
  let tabOptions = state.tabOptions;
  switch (action.type) {
    case "addContinuousImprovementTab":
      let ciTab = tabOptions.find((option: TabOption) => {
        return option.id == "continuous-improvement-insights";
      });

      ciTab
        ? tabOptions
        : tabOptions.splice(0, 0, action.continuousImprovementTabOption);

      return {
        ...state,
        tabOptions: tabOptions
      };
    case "addFacultyTab":
      tabOptions.splice(1, 0, action.facultyTabOption);
      return {
        ...state,
        tabOptions: tabOptions
      };
    case "setReportingYears":
      const defaultReportingYear =
        getReportingYear(action.selectedReportingYearUuid, action.data) ||
        getDefaultReportingYear(state.defaultReportingYear, action.data);
      return {
        ...state,
        defaultReportingYear: defaultReportingYear,
        reportingYears: action.data
      };
    case "setRyApiError":
      return { ...state, ryApiError: action.ryApiError };
    default:
      return state;
  }
};

const getReportingYear = (
  selectedReportingYearUuid: string | null,
  reportingYears: ReportingYear[]
) => {
  if (
    selectedReportingYearUuid === undefined ||
    selectedReportingYearUuid === null
  ) {
    return undefined;
  } else {
    const reportingYear = reportingYears.filter(
      (reportingYear: ReportingYear) =>
        reportingYear.uuid === selectedReportingYearUuid
    );

    return reportingYear[0];
  }
};

const orderedTabIds = ["continuous-improvement-insights", "faculty-insights"];

const showContinuousImprovementTab = (integratedProducts: Product[]) => {
  return checkIfCesProductIntegrated(integratedProducts);
};

export interface OrgDetailContentProps {
  setSelectedTabView: (tabId: string) => void;
}

export default function OrgDetailContent({
  setSelectedTabView
}: OrgDetailContentProps): JSX.Element {
  const { institution, selectedTab, onWmTabSelectedEvent, integratedProducts } =
    useContext<AppContextInterface>(AppContext);

  const navigate = useNavigate();

  const queryString = window.location.search;
  const param = new URLSearchParams(queryString);
  const selectedReportingYearUuid = param.get("reporting_year_uuid");
  const facultyLabel = useIntl().formatMessage({
    id: "app.orgProfileContent.faculty"
  });
  const continuousImprovementLabel = useIntl().formatMessage({
    id: "app.orgProfileContent.countinousImprovement"
  });

  const initialTabOptions: TabOption[] = [];

  const initialState: OrgDetailContentState = {
    tabOptions: initialTabOptions,
    reportingYears: null,
    defaultReportingYear: defaultReportingYear,
    ryApiError: false
  };

  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    const applyResult = (result: ReportingYear[]) => {
      dispatch({
        ...defaultAction,
        type: "setReportingYears",
        data: result,
        selectedReportingYearUuid: selectedReportingYearUuid
      });
    };
    const onError = () => {
      dispatch({ ...defaultAction, type: "setRyApiError", ryApiError: true });
    };
    sendRequest(
      { uri: "/sa/reporting_years", camelize: true },
      applyResult,
      onError
    );
  }, []);

  useEffect(() => {
    const tabParam = new URLSearchParams(window.location.search).get("tab");
    if (state.tabOptions.length > 0 && setSelectedTabView && !tabParam) {
      const firstTabId = orderedTabIds.find((tabId: string) => {
        return state.tabOptions.some(
          (option: TabOption) => option.id === tabId
        );
      });

      if (firstTabId) setSelectedTabView(firstTabId);
    }
  }, [state.tabOptions.length]);

  useEffect(() => {
    if (institution && integratedProducts && integratedProducts.length > 0) {
      if (checkIfFsProductIntegrated(integratedProducts)) {
        setFacultyTab();
      }
      if (showContinuousImprovementTab(integratedProducts)) {
        setContinuousImprovementTab();
      }
    }
  }, [integratedProducts]);

  const setFacultyTab = () => {
    dispatch({
      ...defaultAction,
      type: "addFacultyTab",
      facultyTabOption: {
        name: facultyLabel,
        id: "faculty-insights"
      }
    });
  };

  const setContinuousImprovementTab = () => {
    dispatch({
      ...defaultAction,
      type: "addContinuousImprovementTab",
      continuousImprovementTabOption: {
        name: continuousImprovementLabel,
        id: "continuous-improvement-insights"
      }
    });
  };

  const onWmTabSelect = (event: any) => {
    let params = new URLSearchParams();
    params.append("tab", event.detail.tabId);
    const queryString = window.location.search;
    const param = new URLSearchParams(queryString);
    const locale = param.get("locale");
    if (locale !== null) {
      params.append("locale", locale);
    }
    navigate(`?${params.toString()}`, { replace: true });

    onWmTabSelectedEvent(event);
  };

  return (
    <>
      <div className="tabbar -tabbar">
        <InsightsTabComponent
          selectedTab={selectedTab}
          onWmTabSelected={onWmTabSelect}
          tabOption={state.tabOptions}
        />
      </div>

      <WmTabPanel className="tab-panel" id="faculty" tabId="faculty-insights">
        {checkIfFsProductIntegrated(integratedProducts) && (
          <FacultySuccess
            reportingYears={state.reportingYears}
            defaultReportingYear={state.defaultReportingYear}
            ryApiError={state.ryApiError}
            organizationView={true}
          />
        )}
      </WmTabPanel>

      <WmTabPanel
        className="tab-panel"
        id="continuous-improvement"
        tabId="continuous-improvement-insights"
      >
        {institution && integratedProducts &&
          showContinuousImprovementTab(integratedProducts) && (
            <ContinuousImprovement
              reportingYears={state.reportingYears}
              defaultReportingYear={state.defaultReportingYear}
              ryApiError={state.ryApiError}
              organizationView={true}
            />
          )}
      </WmTabPanel>
    </>
  );
}
